import { forwardRef, useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion, useMotionTemplate, useMotionValue } from "framer-motion";

// --- Helper Functions and Fallbacks ---

// A simple utility for class names, similar to cn/clsx
const cn = (...classes) => {
  return classes.filter(Boolean).join(" ");
}

// Placeholder for image assets if they are not found.
const placeholderImage = (text = "Image") =>
  `https://placehold.co/600x400/1a1a1a/ffffff?text=${text}`

// Noise texture SVG as constant to avoid recreation on every render
const NOISE_TEXTURE_SVG = `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`

// --- Constants ---
const TOTAL_STEPS = 4

const steps = [
  {
    id: "1",
    name: "Connect",
    title: "Find Your People",
    description: "Connect with the rave family, festival crew, old gig mates, or new friends who share your taste. See who's going, sync your calendars, and make every night out a little more social.",
  },
  {
    id: "2",
    name: "Plan",
    title: "Share the Vibe",
    description: "Easily share your event plans or full calendar with friends. Invite, Stay in sync, avoid FOMO, and plan your next big night together without endless group chats.",
  },
  {
    id: "3",
    name: "Find",
    title: "Looking for a ticket",
    description: "Got an event in your diary but haven't managed to find a ticket? Send out a signal to your friends and friends of friends to see if someone has one spare",
  },
  {
    id: "4",
    name: "Resell",
    title: "Need to resell a ticket?",
    description: "Plans changed? Let your network know you have a spare ticket (or two), make someones day, and get some money back too",
  },
]

const ANIMATION_PRESETS = {
  fadeInScale: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
    transition: { type: "spring", stiffness: 300, damping: 25, mass: 0.5 },
  },

  slideInRight: {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
    transition: { type: "spring", stiffness: 300, damping: 25, mass: 0.5 },
  },

  slideInLeft: {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 },
    transition: { type: "spring", stiffness: 300, damping: 25, mass: 0.5 },
  }
}

// --- Hooks ---
function useNumberCycler(totalSteps = TOTAL_STEPS, interval = 5000) {
  const [currentNumber, setCurrentNumber] = useState(0);

  // This effect handles the automatic cycling.
  // It depends on `currentNumber`, so every time the step changes,
  // it will clear the old timer and set a new one for the next step.
  useEffect(() => {
    const timerId = setTimeout(() => {
      setCurrentNumber((prev) => (prev + 1) % totalSteps);
    }, interval);

    // Cleanup function to clear the timer if the component unmounts
    // or if the dependencies of the effect change (e.g., user clicks a step).
    return () => clearTimeout(timerId);
  }, [currentNumber, totalSteps, interval]);

  // This function allows manual setting of the step.
  // When called, it updates `currentNumber`, which will trigger the useEffect
  // to reset the timer for the next cycle.
  const setStep = useCallback((stepIndex) => {
      setCurrentNumber(stepIndex % totalSteps);
  }, [totalSteps]);

  return { currentNumber, setStep };
}


function useIsMobile() {
  // Lazy initialization to prevent hydration mismatch
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia("(max-width: 768px)").matches;
  })

  useEffect(() => {
    // Use matchMedia with proper event listener
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleChange = (e) => {
      setIsMobile(e.matches);
    };

    // Set initial value in effect to ensure client-side accuracy
    setIsMobile(mediaQuery.matches);

    // Use modern addEventListener instead of deprecated addListener
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [])

  return isMobile
}

// --- Components ---
const stepVariants = {
  inactive: { scale: 0.9, opacity: 0.7 },
  active: { scale: 1, opacity: 1 },
}

const StepImage = forwardRef(({ src, alt, className, style, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={className}
      style={{ position: "absolute", userSelect: "none", maxWidth: "unset", ...style }}>
      <img
        alt={alt}
        src={src}
        className="w-full h-full rounded-xl"
        onError={(e) => {
          console.error(`Failed to load carousel image: ${src}`, { alt, error: 'Image load failed' });
          e.currentTarget.src = placeholderImage(alt);
        }}
        {...props} />
      {/* Noise texture overlay on image */}
      <div
        className="absolute inset-0 opacity-[0.15] pointer-events-none rounded-xl"
        style={{
          backgroundImage: NOISE_TEXTURE_SVG,
          backgroundRepeat: 'repeat',
          mixBlendMode: 'overlay',
        }}
      />
    </div>
  );
})
StepImage.displayName = "StepImage"

const MotionStepImage = motion(StepImage)

const AnimatedStepImage = ({
  preset = "fadeInScale",
  delay = 0,
  ...props
}) => {
  const presetConfig = ANIMATION_PRESETS[preset]
  return (
    <MotionStepImage
      {...props}
      {...presetConfig}
      transition={{ ...presetConfig.transition, delay }} />
  );
}

function FeatureCard({
  children,
  step
}) {
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)
  const isMobile = useIsMobile()
  function handleMouseMove({
    currentTarget,
    clientX,
    clientY
  }) {
    if (isMobile) return
    const { left, top } = currentTarget.getBoundingClientRect()
    mouseX.set(clientX - left)
    mouseY.set(clientY - top)
  }
  return (
    <motion.div
      className="animated-cards group relative w-full rounded-2xl"
      onMouseMove={handleMouseMove}
      style={{
        "--x": useMotionTemplate`${mouseX}px`,
        "--y": useMotionTemplate`${mouseY}px`
      }}>
      <div
        className="relative w-full overflow-hidden rounded-3xl border border-white/10 backdrop-blur-md transition-all duration-300"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.05) 50%, rgba(255, 255, 255, 0.08) 100%)',
          boxShadow: 'inset 0 0 80px rgba(255, 255, 255, 0.08), inset 0 0 160px rgba(255, 255, 255, 0.04), inset 0 2px 8px rgba(255, 255, 255, 0.12), 0 20px 60px -10px rgba(0, 0, 0, 0.3)',
        }}>
        {/* Glass shimmer overlay */}
        <div
          className="absolute inset-0 opacity-50 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
          }}
        />
        {/* Noise texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-overlay"
          style={{
            backgroundImage: NOISE_TEXTURE_SVG,
            backgroundRepeat: 'repeat',
          }}
        />
        <div className="m-4 sm:m-6 md:m-10 min-h-[450px] w-full relative z-10">
          {/* Screen reader announcement for auto-rotation */}
          <div className="sr-only" aria-live="polite" aria-atomic="true">
            Now showing step {step + 1} of {TOTAL_STEPS}: {steps[step].name}
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              className="flex w-full flex-col gap-4 md:w-3/5 pr-4 md:pr-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
              <motion.div
                className="text-sm font-semibold uppercase tracking-wider text-sky-400"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05, duration: 0.3, ease: [0.22, 1, 0.36, 1]}}>
                  {steps[step].name}
              </motion.div>
              <motion.h2
                className="text-xl sm:text-2xl font-bold tracking-tight text-white md:text-3xl"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1, duration: 0.3, ease: [0.22, 1, 0.36, 1]}}>
                {steps[step].title}
              </motion.h2>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15, duration: 0.3, ease: [0.22, 1, 0.36, 1]}}>
                <p
                  className="text-sm sm:text-base leading-relaxed text-gray-200">
                  {steps[step].description}
                </p>
              </motion.div>
            </motion.div>
          </AnimatePresence>
          {children}
        </div>
      </div>
    </motion.div>
  );
}

function StepsNav({
  steps: stepItems,
  current,
  onChange
}) {
    return (
      <nav aria-label="Progress" className="flex justify-center px-4">
        <ol
          className="flex w-full flex-wrap items-center justify-center gap-3"
          role="list">
            {stepItems.map((step, stepIdx) => {
                const isCurrent = current === stepIdx;
                return (
                  <motion.li
                    key={step.name}
                    initial="inactive"
                    animate={isCurrent ? "active" : "inactive"}
                    variants={stepVariants}
                    transition={{ duration: 0.3 }}
                    className="relative">
                    <button
                      type="button"
                      className={cn(
                        "group flex items-center gap-3 rounded-full px-4 py-2 text-sm font-medium transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white/20",
                        isCurrent
                            ? "bg-white/10 text-white border border-white/20 backdrop-blur-sm"
                            : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80 border border-white/10"
                      )}
                      onClick={() => {
                        onChange(stepIdx);
                        // Track step navigation with GA4
                        if (window.gtag) {
                          window.gtag('event', 'carousel_step_click', {
                            event_category: 'Feature Carousel',
                            event_label: step.name,
                            step_number: stepIdx + 1,
                            step_name: step.name
                          });
                        }
                      }}>
                        <span
                          className={cn(
                            "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-all duration-300",
                            isCurrent
                                ? "bg-white/20 text-white"
                                : "bg-white/10 text-white/50 group-hover:bg-white/15 group-hover:text-white/70"
                          )}>
                            <span>{stepIdx + 1}</span>
                        </span>
                        <span className="hidden sm:inline-block">{step.name}</span>
                    </button>
                  </motion.li>
                );
            })}
        </ol>
      </nav>
    );
}

const defaultClasses = {
  img: "rounded-xl shadow-2xl shadow-black/40",
  step1img1: "w-[50%] left-0 top-[15%]",
  step1img2: "w-[60%] left-[40%] top-[35%]",
  step2img1: "w-[50%] left-[5%] top-[20%]",
  step2img2: "w-[40%] left-[55%] top-[45%]",
  step3img: "w-[90%] left-[5%] top-[25%]",
  step4img: "w-[90%] left-[5%] top-[25%]"
}

export function FeatureCarousel({
  image,
  step1img1Class = defaultClasses.step1img1,
  step1img2Class = defaultClasses.step1img2,
  step2img1Class = defaultClasses.step2img1,
  step2img2Class = defaultClasses.step2img2,
  step3imgClass = defaultClasses.step3img,
  step4imgClass = defaultClasses.step4img,
  ...props
}) {
  const { currentNumber: step, setStep } = useNumberCycler()
  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <div className="relative w-full h-full">
            <AnimatedStepImage
              alt={image.alt}
              className={cn(defaultClasses.img, step1img1Class)}
              src={image.step1img1}
              preset="slideInLeft" />
            <AnimatedStepImage
              alt={image.alt}
              className={cn(defaultClasses.img, step1img2Class)}
              src={image.step1img2}
              preset="slideInRight"
              delay={0.1} />
          </div>
        );
      case 1:
        return (
          <div className="relative w-full h-full">
            <AnimatedStepImage
              alt={image.alt}
              className={cn(defaultClasses.img, step2img1Class)}
              src={image.step2img1}
              preset="fadeInScale" />
            <AnimatedStepImage
              alt={image.alt}
              className={cn(defaultClasses.img, step2img2Class)}
              src={image.step2img2}
              preset="fadeInScale"
              delay={0.1} />
          </div>
        );
      case 2:
        return (
          <AnimatedStepImage
            alt={image.alt}
            className={cn(defaultClasses.img, step3imgClass)}
            src={image.step3img}
            preset="fadeInScale" />
        );
      case 3:
        return (
          <AnimatedStepImage
            alt={image.alt}
            className={cn(defaultClasses.img, step4imgClass)}
            src={image.step4img}
            preset="fadeInScale" />
        );
      default: return null
    }
  }
  return (
    <div className="flex flex-col gap-8 sm:gap-12 w-full max-w-4xl mx-auto px-2 sm:px-4">
      <FeatureCard {...props} step={step}>
          <AnimatePresence mode="wait">
              <motion.div
                key={step}
                {...ANIMATION_PRESETS.fadeInScale}
                className="w-full h-full absolute">
                  {renderStepContent()}
              </motion.div>
          </AnimatePresence>
      </FeatureCard>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}>
          <StepsNav current={step} onChange={setStep} steps={steps} />
      </motion.div>
    </div>
  );
}